import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  auditorAdd,
  auditorGetById,
  auditorUpdate
} from '../../../store/slices/tenantSlices/auditorSlice';

const AddEditAuditorModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [processOptions, setProcessOptions] = useState([]);

  const initialValues = {
    username: '',
    password: '',
    status: 'true',
    process_ids: []
  };

  const validationSchema = Yup.object({
    username: Yup.string().required('username is required'),
    password: Yup.string().required('password is required'),
    process_ids: Yup.array().min(1, 'process ID is required').required('process ID is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(auditorUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(auditorAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchAuditorById = async () => {
    try {
      const response = await dispatch(auditorGetById(editRecord?.id)).unwrap();
      const process_ids = response?.data?.auditorProcess?.map((x) => x.process_id);
      setValues({
        ...response?.data?.auditor,
        status: String(response?.data?.auditor?.status),
        process_ids: [...process_ids]
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchAuditorById();
    }
  }, [editRecord]);

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getProcessOption();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Auditor' : 'Add Auditor'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="auditor" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="username"
                label="Username"
                placeholder="Enter Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="password"
                label="Password"
                placeholder="Enter Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_ids"
                label="Process"
                placeholder="Select Process"
                value={values.process_ids}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="auditor" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAuditorModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditAuditorModal;
